// `VerifyPage` — US-002 link landing.
//
// DESIGN.md §1.4, §4.4, O6 (3s auto-redirect on success).

import { useEffect, useRef, useState } from 'react';

import { ApiError, useVerifyEmail } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Spinner } from '@barel/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { AuthLayout } from './AuthLayout';
import { emit } from './lib/telemetry';

interface VerifySearch {
  token?: string;
}

type VerifyState =
  | 'loading'
  | 'success'
  | 'error_expired'
  | 'error_invalid'
  | 'error_missing_token'
  | 'error_generic';

const AUTO_REDIRECT_MS = 3_000;

export function VerifyPage() {
  const t = useT('auth');
  const navigate = useNavigate();
  const verify = useVerifyEmail();
  const search = useSearch({ strict: false }) as VerifySearch;

  const [state, setState] = useState<VerifyState>(() =>
    search.token ? 'loading' : 'error_missing_token',
  );
  const [secondsLeft, setSecondsLeft] = useState<number>(AUTO_REDIRECT_MS / 1000);

  const firedRef = useRef<boolean>(false);

  // Trigger the verify mutation exactly once on mount.
  useEffect(() => {
    if (firedRef.current) return;
    if (!search.token) return;
    firedRef.current = true;

    verify
      .mutateAsync({ token: search.token })
      .then(() => {
        emit({ name: 'auth_verify_succeeded' });
        setState('success');
      })
      .catch((err: unknown) => {
        if (ApiError.is(err)) {
          if (err.code === 'verification.token_expired') {
            emit({ name: 'auth_verify_failed', props: { reason: 'expired' } });
            setState('error_expired');
            return;
          }
          if (err.code === 'verification.token_invalid') {
            emit({ name: 'auth_verify_failed', props: { reason: 'invalid' } });
            setState('error_invalid');
            return;
          }
        }
        emit({ name: 'auth_verify_failed', props: { reason: 'other' } });
        setState('error_generic');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect on success after 3s. Per DESIGN.md §4.4 we announce the
  // countdown via aria-live.
  useEffect(() => {
    if (state !== 'success') return;
    setSecondsLeft(AUTO_REDIRECT_MS / 1000);
    const tick = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    const redirect = setTimeout(() => {
      void navigate({ to: '/', replace: true });
    }, AUTO_REDIRECT_MS);
    return () => {
      clearInterval(tick);
      clearTimeout(redirect);
    };
  }, [state, navigate]);

  const renderLoading = () => (
    <div className="flex flex-col items-center gap-4 text-center">
      <Spinner size="lg" tone="accent" aria-label={t('verify.loading.title')} />
      <h1 className="text-xl font-semibold text-primary">{t('verify.loading.title')}</h1>
      <p className="text-sm text-secondary">{t('verify.loading.body')}</p>
    </div>
  );

  const renderSuccess = () => (
    <div className="flex flex-col items-center gap-4 text-center" data-testid="verify-success">
      <div
        aria-hidden="true"
        className="flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: 'var(--success-bg)', color: 'var(--success)' }}
      >
        ✓
      </div>
      <h1 className="text-xl font-semibold text-primary">{t('verify.success.title')}</h1>
      <p className="text-sm text-secondary">{t('verify.success.body')}</p>
      <Button asChild variant="primary" size="lg" fullWidth data-testid="verify-success-cta">
        <Link to="/">{t('verify.success.cta')}</Link>
      </Button>
      <p
        className="text-xs text-tertiary"
        aria-live="polite"
        data-testid="verify-success-countdown"
      >
        {t('verify.success.autoRedirect', { seconds: secondsLeft })}
      </p>
    </div>
  );

  const renderError = (
    body: string,
    cta: { label: string; to: '/auth/verify-pending' | '/auth/signup' | '/auth/login' },
  ) => (
    <div className="flex flex-col gap-4">
      <Banner tone="error" title={t('verify.error.title')}>
        {body}
      </Banner>
      <Button asChild variant="primary" size="lg" fullWidth>
        <Link to={cta.to}>{cta.label}</Link>
      </Button>
    </div>
  );

  return (
    <AuthLayout brandHeadline="brandHeadlineNeutral">
      <section data-route="auth/verify" className="flex flex-col gap-6">
        {state === 'loading' ? renderLoading() : null}
        {state === 'success' ? renderSuccess() : null}
        {state === 'error_expired'
          ? renderError(t('verify.error.expiredBody'), {
              label: t('verify.error.requestNewCta'),
              to: '/auth/verify-pending',
            })
          : null}
        {state === 'error_invalid'
          ? renderError(t('verify.error.invalidBody'), {
              label: t('verify.error.signupCta'),
              to: '/auth/signup',
            })
          : null}
        {state === 'error_missing_token'
          ? renderError(t('verify.error.missingToken'), {
              label: t('verify.error.backCta'),
              to: '/auth/login',
            })
          : null}
        {state === 'error_generic'
          ? renderError(t('verify.error.genericBody'), {
              label: t('verify.error.backCta'),
              to: '/auth/login',
            })
          : null}
      </section>
    </AuthLayout>
  );
}
